import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/PageHeader';

/**
 * Admin Backups — manual backup trigger + list of recent backups with
 * download buttons. Admin / super_admin only. Backups are produced via
 * mongodump on the server and stored at /app/backend/backups (ephemeral),
 * with the latest RETAIN_COUNT (7) kept on disk. Downloading the .gz file
 * to the user's local computer is how durability is guaranteed.
 */
export default function AdminBackups() {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const isSuperAdmin = user?.role === 'super_admin';

  const [info, setInfo] = useState(null);  // {backups, retention, directory, schedule, env}
  const [log, setLog] = useState([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [health, setHealth] = useState(null);
  const [checkingHealth, setCheckingHealth] = useState(false);
  const [verifyFile, setVerifyFile] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [l, lg] = await Promise.all([
        api.get('/admin/backup/list'),
        api.get('/admin/backup/log?limit=20'),
      ]);
      setInfo(l.data);
      setLog(lg.data?.items || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load backups');
    }
  }, []);

  useEffect(() => {
    if (isAdmin) refresh();
  }, [isAdmin, refresh]);

  const runBackup = async () => {
    setRunning(true);
    setError('');
    setSuccess('');
    try {
      const res = await api.post('/admin/backup/run');
      setSuccess(`Backup created: ${res.data.filename} (${res.data.size_mb} MB)`);
      await refresh();
    } catch (e) {
      // Surface the actual mongodump stderr / server message instead of a
      // generic "Backup failed". This makes prod diagnosis 10× easier.
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail ? JSON.stringify(detail) : e.message);
      setError(`Backup failed — ${msg}\n\nTip: click 🩺 Run Health Check below to diagnose.`);
    } finally {
      setRunning(false);
    }
  };

  const runHealthCheck = async () => {
    setCheckingHealth(true);
    setHealth(null);
    try {
      const res = await api.get('/admin/backup/healthcheck');
      setHealth(res.data);
    } catch (e) {
      setError(`Health check failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setCheckingHealth(false);
    }
  };

  const runVerify = async (e) => {
    e?.preventDefault?.();
    if (!verifyFile) return;
    setVerifying(true);
    setVerifyResult(null);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', verifyFile);
      const res = await api.post('/admin/backup/verify', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        maxContentLength: 200 * 1024 * 1024,
        maxBodyLength: 200 * 1024 * 1024,
      });
      setVerifyResult(res.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(`Verify failed: ${typeof detail === 'string' ? detail : (detail ? JSON.stringify(detail) : e.message)}`);
    } finally {
      setVerifying(false);
    }
  };

  const downloadBackup = async (filename) => {
    try {
      const res = await api.get(`/admin/backup/download/${encodeURIComponent(filename)}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/gzip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(`Download failed: ${e.message}`);
    }
  };

  const deleteBackup = async (filename) => {
    if (!window.confirm(`Permanently delete ${filename}?\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/admin/backup/${encodeURIComponent(filename)}`);
      setSuccess(`Deleted ${filename}`);
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Delete failed');
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <PageHeader title="Admin · Backups" subtitle="Restricted" />
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded text-red-800 text-sm">
          This page is restricted to admin / super_admin users.
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4" data-testid="admin-backups-page">
      <PageHeader
        title="Database Backups"
        subtitle="Manual + scheduled mongodump archives. Download to keep them safe on your local computer."
      />

      {error && (
        <div className="p-3 rounded border border-red-300 bg-red-50 text-red-900 text-sm whitespace-pre-wrap" data-testid="backup-error">
          {error}
        </div>
      )}
      {success && (
        <div className="p-3 rounded border border-emerald-300 bg-emerald-50 text-emerald-900 text-sm" data-testid="backup-success">
          {success}
        </div>
      )}

      {/* Info / Schedule */}
      <div className="p-4 bg-white border rounded shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="space-y-1">
            <div className="text-sm flex items-center gap-2">
              <span className="text-gray-500">Environment:</span>
              <code className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-xs font-mono" data-testid="backup-env-badge">
                {info?.env || '—'}
              </code>
              <span className="text-xs text-gray-400">(audit log + file list are scoped to this env)</span>
            </div>
            <div className="text-sm">
              <span className="text-gray-500">Schedule: </span>
              <b>{info?.schedule || '—'}</b>
            </div>
            <div className="text-sm">
              <span className="text-gray-500">Retention: </span>
              <b>last {info?.retention || '?'} backups</b>
              <span className="text-xs text-gray-500 ml-2">(older auto-pruned after each backup)</span>
            </div>
            <div className="text-xs text-gray-500">
              Backups are stored on the server temporarily. Download the .gz file to your
              computer for permanent safekeeping — the server disk is reset on every redeploy.
            </div>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <button
              onClick={runBackup}
              disabled={running}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded text-sm font-semibold shadow-sm"
              data-testid="backup-run-btn"
            >
              {running ? '⏳ Backing up…' : '💾 Backup Now'}
            </button>
            <button
              onClick={runHealthCheck}
              disabled={checkingHealth}
              className="px-3 py-1.5 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 rounded text-xs"
              data-testid="backup-healthcheck-btn"
            >
              {checkingHealth ? '⏳ Checking…' : '🩺 Run Health Check'}
            </button>
          </div>
        </div>
      </div>

      {/* Health-check result */}
      {health && (
        <div className={`p-4 border rounded shadow-sm ${
          health.status === 'healthy'
            ? 'bg-emerald-50 border-emerald-300'
            : 'bg-amber-50 border-amber-300'
        }`} data-testid="backup-health-panel">
          <h3 className="font-semibold mb-2">
            🩺 Health Check —{' '}
            <span className={health.status === 'healthy' ? 'text-emerald-700' : 'text-amber-700'}>
              {health.status}
            </span>
            <span className="ml-3 text-xs font-normal text-gray-600">
              Active strategy: <code className="bg-white px-1 rounded">{health.strategy}</code>
            </span>
          </h3>
          <div className="text-xs grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 font-mono">
            <div>
              <span className="text-gray-500">mongodump available:</span>{' '}
              {health.checks.mongodump_available
                ? <span className="text-emerald-700">✓ {health.checks.mongodump_path}</span>
                : <span className="text-amber-700">⚠ not installed — using Python fallback (still works)</span>}
            </div>
            <div>
              <span className="text-gray-500">mongodump version:</span>{' '}
              {health.checks.mongodump_version || '—'}
            </div>
            <div>
              <span className="text-gray-500">backup dir writable:</span>{' '}
              {health.checks.backup_dir_writable
                ? <span className="text-emerald-700">✓ {health.checks.backup_dir}</span>
                : <span className="text-red-700">✗ {health.checks.backup_dir_error || 'denied'}</span>}
            </div>
            <div>
              <span className="text-gray-500">mongo connection:</span>{' '}
              {health.checks.mongo_connection_ok
                ? <span className="text-emerald-700">✓ ok</span>
                : <span className="text-red-700">✗ {health.checks.mongo_error}</span>}
            </div>
          </div>
        </div>
      )}

      {/* Backups list */}
      <div className="bg-white border rounded shadow-sm">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="font-semibold">Recent backups</h3>
          <span className="text-xs text-gray-500">{info?.backups?.length || 0} files on disk</span>
        </div>
        {(!info?.backups || info.backups.length === 0) ? (
          <div className="p-6 text-sm text-gray-500 text-center">
            No backups yet. Click <b>Backup Now</b> above to create one.
          </div>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="px-3 py-2 text-left">Filename</th>
                  <th className="px-3 py-2 text-left">Format</th>
                  <th className="px-3 py-2 text-left">Created</th>
                  <th className="px-3 py-2 text-right">Size</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {info.backups.map((b) => (
                  <tr key={b.filename} className="border-b hover:bg-gray-50" data-testid={`backup-row-${b.filename}`}>
                    <td className="px-3 py-2 font-mono text-xs break-all">{b.filename}</td>
                    <td className="px-3 py-2 text-xs">
                      <span className={`px-1.5 py-0.5 rounded ${b.format === 'json.gz' ? 'bg-indigo-50 text-indigo-700' : 'bg-emerald-50 text-emerald-700'}`}>
                        {b.format || 'mongodump.gz'}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtTime(b.created_at)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{b.size_mb} MB</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => downloadBackup(b.filename)}
                        className="text-blue-700 hover:text-blue-900 mr-3 text-xs font-medium"
                        data-testid={`backup-download-${b.filename}`}
                      >
                        📥 Download
                      </button>
                      {isSuperAdmin && (
                        <button
                          onClick={() => deleteBackup(b.filename)}
                          className="text-red-700 hover:text-red-900 text-xs font-medium"
                          data-testid={`backup-delete-${b.filename}`}
                        >
                          🗑 Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Verify backup contents */}
      <div className="p-4 bg-white border rounded shadow-sm" data-testid="backup-verify-section">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div>
            <h3 className="font-semibold">🔎 Verify a backup file</h3>
            <p className="text-xs text-gray-600">
              Upload a downloaded <code>.json.gz</code> backup to see its metadata + per-collection
              document counts. Useful for confirming a backup captured production data, not preview.
              <br />
              Files are read in memory only — nothing is written to the database.
            </p>
          </div>
        </div>
        <form onSubmit={runVerify} className="flex flex-wrap items-center gap-2">
          <input
            type="file"
            accept=".gz,.json.gz,application/gzip"
            onChange={(e) => { setVerifyFile(e.target.files?.[0] || null); setVerifyResult(null); }}
            className="text-sm border rounded px-2 py-1.5 flex-1 min-w-[260px]"
            data-testid="backup-verify-file"
          />
          <button
            type="submit"
            disabled={!verifyFile || verifying}
            className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white rounded text-sm"
            data-testid="backup-verify-btn"
          >
            {verifying ? '⏳ Verifying…' : '🔎 Verify'}
          </button>
        </form>

        {verifyResult && (
          <div className="mt-4 space-y-2" data-testid="backup-verify-result">
            <div className="p-3 bg-gray-50 border rounded text-xs grid grid-cols-1 md:grid-cols-3 gap-1 font-mono">
              <div><span className="text-gray-500">File:</span> {verifyResult.filename}</div>
              <div><span className="text-gray-500">Size:</span> {verifyResult.size_mb} MB</div>
              <div><span className="text-gray-500">Documents:</span> <b>{verifyResult.document_count}</b> in {verifyResult.collection_count} collections</div>
              <div><span className="text-gray-500">Source env:</span> <b>{verifyResult.meta?.env || '—'}</b></div>
              <div><span className="text-gray-500">Source DB:</span> {verifyResult.meta?.db_name || '—'}</div>
              <div><span className="text-gray-500">Exported:</span> {verifyResult.meta?.exported_at || '—'}</div>
            </div>
            <div className="overflow-auto max-h-96 border rounded">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-gray-100">
                  <tr>
                    <th className="px-3 py-1.5 text-left">Collection</th>
                    <th className="px-3 py-1.5 text-right">Docs</th>
                    <th className="px-3 py-1.5 text-left">First 3 samples (truncated)</th>
                  </tr>
                </thead>
                <tbody>
                  {verifyResult.collections.map((c) => (
                    <tr key={c.collection} className={`border-b ${c.important ? 'bg-amber-50' : ''}`}>
                      <td className="px-3 py-1 font-mono">
                        {c.important && <span title="key business collection" className="mr-1">⭐</span>}
                        {c.collection}
                      </td>
                      <td className="px-3 py-1 text-right font-semibold">{c.doc_count}</td>
                      <td className="px-3 py-1">
                        {c.samples.length === 0 ? (
                          <span className="text-gray-400">empty</span>
                        ) : (
                          <div className="space-y-0.5">
                            {c.samples.map((s, i) => (
                              <div key={i} className="font-mono text-gray-700 truncate">
                                {Object.entries(s).slice(0, 5).map(([k, v]) => (
                                  <span key={k} className="mr-2"><span className="text-gray-400">{k}:</span> {String(v)}</span>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Audit log */}
      <div className="bg-white border rounded shadow-sm">
        <div className="px-4 py-3 border-b">
          <h3 className="font-semibold">Audit log (last 20 runs)</h3>
        </div>
        {log.length === 0 ? (
          <div className="p-4 text-sm text-gray-500">No runs logged yet.</div>
        ) : (
          <div className="overflow-auto max-h-96">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b sticky top-0">
                  <th className="px-3 py-2 text-left">Started</th>
                  <th className="px-3 py-2 text-left">Trigger</th>
                  <th className="px-3 py-2 text-left">By</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-right">Size</th>
                  <th className="px-3 py-2 text-left">Filename / Error</th>
                </tr>
              </thead>
              <tbody>
                {log.map((row) => (
                  <tr key={row.id} className="border-b">
                    <td className="px-3 py-1.5 whitespace-nowrap font-mono">{fmtTime(row.started_at || row.performed_at)}</td>
                    <td className="px-3 py-1.5">{row.trigger}</td>
                    <td className="px-3 py-1.5">{row.triggered_by || '—'}</td>
                    <td className="px-3 py-1.5">
                      <span className={
                        row.status === 'success'
                          ? 'text-emerald-700 font-semibold'
                          : 'text-red-700 font-semibold'
                      }>
                        {row.status}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right whitespace-nowrap">
                      {row.size_mb ? `${row.size_mb} MB` : '—'}
                    </td>
                    <td className="px-3 py-1.5 break-all">
                      {row.status === 'success' ? (
                        <span className="font-mono text-gray-600">{row.filename}</span>
                      ) : (
                        <span className="text-red-700">{row.error}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
